import { useCallback, useRef } from "react";

import { fetchCalculatedLinearFeet } from "~/features/quote/hooks/use-item-calculation.tsx";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type {
	ShipmentFreightDetails,
	ShipmentLineItem,
} from "~/shared/schemas/shipment-management-schema.ts";

import { buildLinearFeetRequestBody } from "../utils/calculation-utils.ts";

/**
 * Returns a callback that recalculates linear feet for the given line items and
 * writes the result onto the freight draft. No-ops outside of edit mode, and
 * discards responses that a newer request has already superseded.
 */
export const useLinearFeetCalculation = ({
	isEditing,
	updateFreightDetails,
}: {
	isEditing: boolean;
	updateFreightDetails: (
		patch: Partial<Omit<ShipmentFreightDetails, "lineItems">>,
	) => void;
}) => {
	const { showError } = useNotificationActions();
	const latestRequestIdRef = useRef(0);

	return useCallback(
		async (lineItems: ShipmentLineItem[]) => {
			if (!isEditing) {
				return;
			}

			latestRequestIdRef.current += 1;
			const requestId = latestRequestIdRef.current;

			const linearFeetRequestBody = buildLinearFeetRequestBody(lineItems);
			if (linearFeetRequestBody.length === 0) {
				updateFreightDetails({ calculatedLinearFeet: null });
				return;
			}

			try {
				const calculated = await fetchCalculatedLinearFeet(
					linearFeetRequestBody,
				);
				if (requestId !== latestRequestIdRef.current) {
					return;
				}
				updateFreightDetails({ calculatedLinearFeet: calculated });
			} catch {
				if (requestId !== latestRequestIdRef.current) {
					return;
				}
				updateFreightDetails({ calculatedLinearFeet: null });
				showError("Unable to calculate linear feet. Please try again.");
			}
		},
		[isEditing, updateFreightDetails, showError],
	);
};
